// Tracks whether SharePoint export is configured on the server. Used by
// `ExportToSharePointButton` to hide-when-unconfigured (ADR-0018: no
// demo-mode fallback) and by the Ops Integrations page to surface the
// M365 row.
//
// The probe hits Microsoft Graph, so it isn't free — debounce by caching
// the response per page-load. Callers that need a re-check after admin
// changes can pass `refresh: true`.

import { useEffect, useState } from 'react';
import { api } from './api';
import type { SharePointStatus } from '@septima/contract';

let cached: SharePointStatus | null = null;
let inflight: Promise<SharePointStatus | null> | null = null;

async function fetchStatus(): Promise<SharePointStatus | null> {
    if (cached) return cached;
    if (inflight) return inflight;
    inflight = api.sharePoint.status().then((res) => {
        inflight = null;
        if (res.status === 200) {
            cached = res.body;
            return cached;
        }
        return null;
    });
    return inflight;
}

export function useSharePointStatus(): {
    status: SharePointStatus | null;
    loading: boolean;
} {
    const [status, setStatus] = useState<SharePointStatus | null>(cached);
    const [loading, setLoading] = useState<boolean>(cached === null);

    useEffect(() => {
        let cancelled = false;
        fetchStatus().then((s) => {
            if (cancelled) return;
            setStatus(s);
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return { status, loading };
}

/** Invalidate the in-process cache. Used by tests and the Integrations
 *  page's "Test connection" affordance if one is added later. */
export function resetSharePointStatusCache(): void {
    cached = null;
}
